import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useSelector, useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import DataLoader from "../components/globalView/DataLoader";
import {
    getWhitelistedWebsites,
    addWhitelistedWebsite,
    deleteWhitelistedWebsite,
    handleLoadState
} from "../store/actions";

const isValidURL = (url) => {
    const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
    return urlRegex.test(url);
};

const WhitelistedWebsites = () => {
    const dispatch = useDispatch();
    const [newUrl, setNewUrl] = useState('');
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isAddingUrl, setIsAddingUrl] = useState(false);
    const [localUrls, setLocalUrls] = useState([]);

    const isLoading = useSelector((state) => state.userReducer.loading);
    const whitelistedWebsites = useSelector((state) => state.userReducer.whitelistedWebsites || []);

    useEffect(() => {
        dispatch(handleLoadState(true));
        dispatch(getWhitelistedWebsites()).finally(() => dispatch(handleLoadState(false)));
    }, [dispatch]);

    useEffect(() => {
        setLocalUrls(whitelistedWebsites);
    }, [whitelistedWebsites]);

    const addUrl = () => {
        if (isValidURL(newUrl)) {
            if (localUrls.includes(newUrl)) {
                setError('This URL is already in the list.');
            } else {
                setError('');
                setIsAddingUrl(true);
                dispatch(addWhitelistedWebsite(newUrl))
                    .then(() => {
                        setNewUrl('');
                        setSuccessMessage('URL added successfully');
                        setTimeout(() => setSuccessMessage(''), 2000);
                    })
                    .catch(() => {
                        setError('Failed to add URL. Please try again.');
                    })
                    .finally(() => {
                        setIsAddingUrl(false);
                    });
            }
        } else {
            setError('Please enter a valid URL.');
        }
    };

    const deleteUrl = (urlToDelete) => {
        // Optimistic update
        setLocalUrls(prevUrls => prevUrls.filter(url => url !== urlToDelete));
        dispatch(deleteWhitelistedWebsite(urlToDelete))
            .then(() => {
                setSuccessMessage('URL deleted successfully');
                setTimeout(() => setSuccessMessage(''), 2000);
            })
            .catch(() => {
                // Revert optimistic update
                setLocalUrls(prevUrls => [...prevUrls, urlToDelete]);
                setError('Failed to delete URL. Please try again.');
                setTimeout(() => setError(''), 2000);
            });
    };

    if (isLoading) {
        return <DataLoader />;
    }

    return (
        <>
            <Helmet>
                <title>Website Whitelist Management | Instaswap Partners</title>
            </Helmet>
            <div>
                <div className="secHdr">
                    <h2 className="secTitle">Whitelisted Websites</h2>
                    <p className="secLead">
                        Website whitelisting for widget integration. The widget will only
                        load on the origins listed here. Requests from other domains are
                        blocked.
                    </p>
                </div>

                <div className="wlAddBar">
                    <input
                        className="input"
                        type="text"
                        value={newUrl}
                        onChange={(e) => setNewUrl(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") addUrl();
                        }}
                        placeholder="Add a website URL, e.g. https://app.yourdomain.com"
                    />
                    <button
                        type="button"
                        className="btnPrimary"
                        style={{ height: 48 }}
                        onClick={addUrl}
                        disabled={isAddingUrl}
                    >
                        {isAddingUrl ? <FontAwesomeIcon icon={faSpinner} spin /> : "Add new URL"}
                    </button>
                </div>

                {error && (
                    <div className="notice red" style={{ marginBottom: 14, maxWidth: 680 }}>
                        <span className="ni">⚠</span>
                        <div>{error}</div>
                    </div>
                )}
                {successMessage && (
                    <div className="notice green" style={{ marginBottom: 14, maxWidth: 680 }}>
                        <span className="ni">✓</span>
                        <div>{successMessage}</div>
                    </div>
                )}

                <div className="gtScroll" style={{ maxWidth: 860 }}>
                    <div
                        className="gtHead"
                        style={{ gridTemplateColumns: "1.4fr 130px 80px" }}
                    >
                        <span>Origin</span>
                        <span>Status</span>
                        <span className="r"></span>
                    </div>
                    {localUrls.length > 0 ? (
                        localUrls.map((url) => (
                            <div
                                key={url}
                                className="gtRow"
                                style={{ gridTemplateColumns: "1.4fr 130px 80px", minHeight: 58 }}
                            >
                                <div className="cellMono" style={{ fontSize: 13.5, color: "#eef3f0" }}>
                                    {url}
                                </div>
                                <div>
                                    <span className="pill done">
                                        <span className="pd" />
                                        Active
                                    </span>
                                </div>
                                <div className="r">
                                    <button
                                        type="button"
                                        className="iconBtn danger"
                                        onClick={() => deleteUrl(url)}
                                        aria-label={`Delete ${url}`}
                                    >
                                        <FontAwesomeIcon icon={faTrashAlt} />
                                    </button>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="gtEmpty">No URLs whitelisted yet.</div>
                    )}
                </div>

                <p className="wlHint">
                    Include the full origin with scheme. Only requests from whitelisted
                    URLs will be accepted.
                </p>
            </div>
        </>
    );
};

export default WhitelistedWebsites;
